import React from "react";
import "./order-tracking.css";

const preloaderSrc = `${process.env.PUBLIC_URL || ""}/assets/images/icon/preloader.gif`;

const hasValue = (value) => value !== null && value !== undefined && value !== "";

const unwrapData = (value) => {
  if (Array.isArray(value)) {
    return unwrapData(value[0]);
  }
  if (value?.data && value.data !== value) {
    return unwrapData(value.data);
  }
  if (value?.response_data && value.response_data !== value) {
    return unwrapData(value.response_data);
  }
  return value || {};
};

const getFirstValue = (sources, keys, fallback = "") => {
  for (const source of sources) {
    if (!source || typeof source !== "object") continue;

    for (const key of keys) {
      if (hasValue(source[key])) {
        return source[key];
      }
    }
  }

  return fallback;
};

const getFirstObject = (source, keys) => {
  if (!source || typeof source !== "object") return null;

  for (const key of keys) {
    if (source[key] && typeof source[key] === "object" && !Array.isArray(source[key])) {
      return source[key];
    }
  }

  return null;
};

const getFirstArray = (sources, keys) => {
  for (const source of sources) {
    if (!source || typeof source !== "object") continue;

    for (const key of keys) {
      if (Array.isArray(source[key])) {
        return source[key];
      }
    }
  }

  return [];
};

const parseTimelineState = (value) => {
  if (!hasValue(value)) return null;
  if (value === true || value === 1 || value === "1") return true;
  if (value === false || value === 0 || value === "0") return false;

  const normalizedValue = String(value).trim().toLowerCase();

  if (["true", "yes", "done", "completed", "complete", "active"].includes(normalizedValue)) {
    return true;
  }
  if (["false", "no", "pending", "inactive", "not_done", "not done"].includes(normalizedValue)) {
    return false;
  }

  return null;
};

const getTimelineState = (item) => {
  const stateKeys = [
    "active",
    "is_active",
    "completed",
    "is_completed",
    "checked",
    "is_checked",
    "done",
    "is_done",
    "is_reached",
    "reached",
    "status_done",
    "order_status_value",
    "status_value",
    "is_status",
  ];

  for (const key of stateKeys) {
    const parsedState = parseTimelineState(item?.[key]);
    if (parsedState !== null) {
      return parsedState;
    }
  }

  const statusValue = getFirstValue(
    [item],
    ["status"],
    ""
  );

  return parseTimelineState(statusValue);
};

const normalizeText = (value) => String(value || "").trim().toLowerCase();

const isSameStatus = (status, currentStatus) => {
  const statusText = normalizeText(status);
  const currentStatusText = normalizeText(currentStatus);

  return (
    statusText &&
    currentStatusText &&
    (statusText === currentStatusText ||
      statusText.includes(currentStatusText) ||
      currentStatusText.includes(statusText))
  );
};

const normalizeTimelineItem = (item, index) => {
  if (typeof item === "string") {
    return {
      title: item,
      date: "",
      active: true,
    };
  }

  return {
    title: getFirstValue(
      [item],
      [
        "status_text",
        "title",
        "status_title",
        "status_name",
        "order_status_name",
        "order_status_title",
        "order_status",
        "status",
        "status_label",
        "name",
        "label",
      ],
      `Step ${index + 1}`
    ),
    date: getFirstValue(
      [item],
      [
        "date_time",
        "datetime",
        "status_datetime",
        "status_date_time",
        "created_at",
        "updated_at",
        "status_time",
        "time",
        "status_date",
        "date",
      ],
      ""
    ),
    amount: getFirstValue([item], ["amount", "total_amount", "order_amount", "grand_total"], ""),
    paymentMethod: getFirstValue([item], ["payment_method", "payment_type", "payment_mode"], ""),
    current: parseTimelineState(item?.is_current ?? item?.current),
    active: getTimelineState(item),
  };
};

const OrderTracking = ({ trackingData, loading = false, error = "", orderId = "" }) => {
  const rawData = unwrapData(trackingData);
  const orderData =
    getFirstObject(rawData, [
      "order_details",
      "order_detail",
      "order",
      "tracking_detail",
      "track_order_detail",
    ]) || rawData;
  const driverData =
    getFirstObject(rawData, ["driver", "driver_detail", "delivery_boy", "rider"]) ||
    getFirstObject(orderData, ["driver", "driver_detail", "delivery_boy", "rider"]) ||
    {};
  const receiptData =
    getFirstObject(orderData, ["receipt", "receipt_details", "order_receipt"]) ||
    getFirstObject(rawData, ["receipt", "receipt_details", "order_receipt"]) ||
    {};
  const dataSources = [orderData, rawData];
  const orderNo = getFirstValue(dataSources, ["order_no", "order_number", "order_id"], orderId);
  const orderDate = getFirstValue(dataSources, ["order_date", "date", "created_at", "created_date"], "");
  const orderTime = getFirstValue(dataSources, ["order_time", "time", "created_time"], "");
  const currentStatus = getFirstValue(
    dataSources,
    [
      "current_status_text",
      "order_status_text",
      "status_text",
      "current_status",
      "order_status",
      "status_name",
      "status",
    ],
    "Order Status"
  );
  const currency = getFirstValue([receiptData, ...dataSources], ["currency", "currency_symbol"], "INR");
  const amount = getFirstValue(
    [receiptData, orderData, rawData],
    [
      "order_price",
      "total_amount",
      "grand_total",
      "order_amount",
      "amount",
      "cart_amount",
      "net_amount",
      "payable_amount",
    ],
    ""
  );
  const paymentMethod = getFirstValue(dataSources, ["payment_method", "payment_type", "payment_mode"], "");
  const distance = getFirstValue(dataSources, ["distance_text", "distance", "distance_km"], "");
  const duration = getFirstValue(dataSources, ["duration_text", "duration", "eta", "estimated_time"], "");
  const successMessage = getFirstValue(
    dataSources,
    ["message", "tracking_message", "status_message"],
    ""
  );
  const driverName = getFirstValue(
    [driverData, orderData],
    ["driver_name", "delivery_boy_name", "rider_name", "name"],
    currentStatus
  );
  const driverPhone = getFirstValue(
    [driverData, orderData],
    ["driver_phone", "driver_mobile", "delivery_boy_phone", "mobile", "phone"],
    ""
  );
  const timelineSource = getFirstArray([rawData, orderData], [
    "order_status_holders",
    "timeline",
    "tracking",
    "tracking_data",
    "tracking_details",
    "order_tracking",
    "track_order",
    "status_history",
    "order_status_logs",
    "logs",
  ]);
  const normalizedTimelineItems = (
    timelineSource.length
      ? timelineSource
      : [{ order_status: currentStatus, date_time: orderDate || orderTime }]
  ).map(normalizeTimelineItem);
  const hasTimelineState = normalizedTimelineItems.some((item) => item.active !== null);
  const currentItemIndex = normalizedTimelineItems.findIndex((item) => item.current === true);
  const currentStatusIndex =
    currentItemIndex >= 0
      ? currentItemIndex
      : normalizedTimelineItems.findIndex((item) => isSameStatus(item.title, currentStatus));
  const timelineItems = normalizedTimelineItems.map((item, index) => ({
    ...item,
    active:
      item.active ??
      (hasTimelineState
        ? false
        : currentStatusIndex >= 0
        ? index <= currentStatusIndex
        : true),
  }));
  const currentTimelineItem = timelineItems.find((item) => item.active) || timelineItems[0] || {};
  const latitude = getFirstValue(dataSources, ["latitude", "lat", "delivery_latitude", "drop_latitude"], "");
  const longitude = getFirstValue(dataSources, ["longitude", "lng", "delivery_longitude", "drop_longitude"], "");
  const mapAddress = getFirstValue(
    dataSources,
    ["drop_off", "delivery_address", "address", "pickup", "store_address"],
    "New Delhi"
  );
  const mapQuery = latitude && longitude ? `${latitude},${longitude}` : mapAddress;
  const mapSrc = `https://www.google.com/maps?q=${encodeURIComponent(mapQuery)}&output=embed`;
  const distanceText = [distance, duration].filter(Boolean).join(" | ");
  const dateText = [orderDate, orderTime].filter(Boolean).join(" | ");
  const summaryAmount = amount;
  const summaryPaymentMethod = paymentMethod || currentTimelineItem.paymentMethod;
  const summaryStatus =
    currentStatus && currentStatus !== "Order Status"
      ? currentStatus
      : currentTimelineItem.title || driverName || "Order Status";
  const amountText = summaryAmount ? `${currency} ${summaryAmount}` : "";

  if (loading) {
    return (
      <div className="tracking-loader-overlay">
        <div className="tracking-loader-box">
          <img src={preloaderSrc} alt="Loading" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid bg-light py-20">
        <div className="card tracking-card p-20 text-danger">{error}</div>
      </div>
    );
  }

  return (
    <div className="container-fluid tracking-page-wrap py-20">
      <div className="row g-3">

        {/* LEFT : ORDER STATUS */}
        <div className="col-lg-4 col-12">
          <div className="card tracking-card">

            {/* Header */}
            <div className="tracking-header">
              <div>
                <h6 className="fw-bold mb-1">Order #{orderNo}</h6>
                {dateText && <small>{dateText}</small>}
              </div>

              {distanceText && (
                <span className="badge bg-light text-dark">
                  {distanceText}
                </span>
              )}
            </div>

            {/* Driver Info */}
            <div className="tracking-summary-box">
              <div className="driver-left">
                <div className="driver-avatar">
                  <span></span>
                </div>
                <div>
                  <div className="tracking-summary-status">
                    {summaryStatus}
                  </div>
                  {amountText && <small className="tracking-summary-amount">{amountText}</small>}
                </div>
              </div>

              <div className="driver-actions">
                {driverPhone && (
                  <a href={`tel:${driverPhone}`} className="tracking-call-btn">
                    Call
                  </a>
                )}
                {summaryPaymentMethod && (
                  <small className="tracking-payment-method">{summaryPaymentMethod}</small>
                )}
              </div>
            </div>

            {successMessage && (
              <p className="success-msg">
                {successMessage}
              </p>
            )}

            {/* Timeline */}
            <ul className="timeline">
              {timelineItems.map((item, index) => (
                <li
                  key={`${item.title}-${index}`}
                  className={item.active ? "active" : "pending"}
                >
                  <span className="dot"></span>
                  <div>
                    <strong>{item.title}</strong>
                    {item.date && <small>{item.date}</small>}
                  </div>
                </li>
              ))}
            </ul>

          </div>
        </div>

        {/* RIGHT : MAP */}
        <div className="col-lg-8 col-12">
          <div className="card map-card">
            <iframe
              title="map"
              src={mapSrc}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
            ></iframe>
          </div>
        </div>

      </div>
    </div>
  );
};

export default OrderTracking;
